import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetConfigPortalCredentialsDTO } from "@src/domain/dto/config";
import type { ConfigPortalCredential } from "@src/domain/entities/config";
import { GetConfigPortalCredentialsUseCase } from "@src/application/useCases/config";
import { ConfigPortalCredentialRepository } from "@src/infrastructure/repositories/config";
import { useConfigListQuery } from "./useConfigListQuery";

interface ConfigPortalCredentialsDataContextValue {
  brandKey: string;
  credentials: ConfigPortalCredential[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const ConfigPortalCredentialsDataContext =
  createContext<ConfigPortalCredentialsDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useConfigPortalCredentials(): ConfigPortalCredentialsDataContextValue {
  const context = useContext(ConfigPortalCredentialsDataContext);
  if (!context) {
    throw new Error(
      "useConfigPortalCredentials must be used within ConfigPortalCredentialsDataProvider",
    );
  }
  return context;
}

interface ConfigPortalCredentialsDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function ConfigPortalCredentialsDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<ConfigPortalCredentialsDataProviderProps>) {
  const getUseCase = useMemo(
    () =>
      new GetConfigPortalCredentialsUseCase(
        new ConfigPortalCredentialRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getUseCase.execute(
        new GetConfigPortalCredentialsDTO({
          search: args.search,
          includeInactive: true,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getUseCase],
  );

  const {
    items: credentials,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useConfigListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load portal credentials",
    cacheScope: "portal-credentials",
    fetchPage,
  });

  const value = useMemo(
    (): ConfigPortalCredentialsDataContextValue => ({
      brandKey,
      credentials,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      credentials,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <ConfigPortalCredentialsDataContext.Provider value={value}>
      {children}
    </ConfigPortalCredentialsDataContext.Provider>
  );
}
